import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { highlightSearchTerm } from '../../../components/custom_components/Search/Search'
import { CompanyTiny } from '../../../types'
import { Select, Spin } from 'antd'

const { Option } = Select

interface CompanySearchSelectProps {
  searchTerm: string
  onSearch: (value: string) => void
  onClear: () => void
  companies: CompanyTiny[]
  loading: boolean
  shouldShowOptions: boolean
  onOptionClick: (company: CompanyTiny) => void
  placeholder: string
  selectedCompany: CompanyTiny | null
  size?: 'small' | 'large' | 'default'
  disabled?: boolean
  optionMaxWidth?: number
}

export const CompanySearchSelect: FC<CompanySearchSelectProps> = ({
  searchTerm,
  onSearch,
  onClear,
  companies,
  loading,
  shouldShowOptions,
  onOptionClick,
  placeholder,
  selectedCompany,
  size = 'small',
  disabled = false,
  optionMaxWidth,
}) => {
  const { t } = useTranslation()

  // Render function for company options
  const renderOptions = useCallback(
    (company: CompanyTiny) => (
      <Option key={`company-${company.id}`} value={company.id} label={company.name}>
        <div className="option" onClick={() => onOptionClick(company)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span className="option-text" style={{ ...(optionMaxWidth !== undefined ? { maxWidth: `${optionMaxWidth}px` } : {}) }}>
              {highlightSearchTerm(company.name || company.id, searchTerm)}
            </span>
          </div>
        </div>
      </Option>
    ),
    [onOptionClick, searchTerm, optionMaxWidth],
  )

  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
          <Spin size="small" style={{ marginRight: '8px' }} />
          <span>{t('navigation.search.loading')}</span>
        </div>
      )
    } else if (searchTerm.length > 1) {
      return t('navigation.search.noResults')
    } else if (searchTerm.length > 0 && searchTerm.length <= 1) {
      return t('navigation.search.startTyping')
    }
    return t('navigation.search.startTyping')
  }, [searchTerm, loading, t])

  return (
    <Select
      showSearch
      placeholder={placeholder}
      value={
        selectedCompany
          ? {
              key: selectedCompany.id,
              label: selectedCompany.name,
            }
          : undefined
      }
      labelInValue
      onSearch={onSearch}
      onChange={(value) => {
        if (value && typeof value === 'object' && value.key) {
          const company = companies.find((comp) => comp.id === value.key)
          if (company) {
            onOptionClick(company)
          }
        } else {
          onClear()
        }
      }}
      allowClear
      filterOption={false}
      loading={loading}
      notFoundContent={notFoundContent}
      style={{ width: '100%' }}
      size={size}
      dropdownMatchSelectWidth={false}
      disabled={disabled}
    >
      {shouldShowOptions &&
        companies
          .filter((company) => {
            if (selectedCompany && company.id === selectedCompany.id) {
              return false
            }
            return true
          })
          .map(renderOptions)}
    </Select>
  )
}
